import { getPositionTextV2, getTitleText } from '@/Overtime/utils/marketsV2'
import { deflate } from 'pako'
import { formatMarketOdds } from '@/Overtime/utils/markets'
import { OddsType } from "@/Overtime/enums/markets"
import type { Chain } from 'viem'

type OvertimeBetData = {
  gameId: string
  leagueId: number
  typeId: number
  position: number
  homeTeam: string
  awayTeam: string
  chain: Chain | undefined
  ticketId: string
  line?: number
  playerId?: number
  playerName?: string
  market: {
    homeTeam: string
    awayTeam: string
    leagueId: number
    leagueName: string
    id: string
    gameId: string
    slug: string
    title: string
    participants: Array<{ name: string }>
    startsAt: number
    sport: {
      sportId: string
      name: string
      slug: string
    }
    league: {
      id: number
      name: string
      slug: string
      tournamentName?: string
    }
    country: {
      name: string
      slug: string
    }
    line: number
    type: string
    typeId: number
    odds: number[]
    positionNames?: string[]
    // Add critical validation fields
    maturity: number
    maturityDate: string
    isOpen: boolean
    isResolved: boolean
    isCancelled: boolean
    isPaused: boolean
    isOneSideMarket: boolean
    isPlayerPropsMarket: boolean
    isOneSidePlayerPropsMarket: boolean
    isYesNoPlayerPropsMarket: boolean
    playerProps: {
      playerId: number
      playerName: string
    }
    combinedPositions: any[][]
  }
  betAmount: string
  totalOdds: number
  betToken: string
  isBatch?: boolean
  isCombo?: boolean
  live?: boolean
  selectedCollateralSymbol?: string
}

interface OvertimeDiscordEmbed {
  title: string
  description: string
  fields: { name: string; value: string; inline?: boolean }[]
  color?: number
  thumbnail?: { url: string }
  footer?: { text: string; icon_url: string }
  timestamp?: string
}

const createOvertimeBetslipData = (bet: OvertimeBetData) => {
  return {
    gameId: bet.gameId,
    leagueId: bet.leagueId,
    typeId: bet.typeId,
    position: bet.position,
    homeTeam: bet.homeTeam,
    awayTeam: bet.awayTeam,
    chain: bet.chain,
    market: bet.market,
    betAmount: bet.betAmount,
    totalOdds: bet.totalOdds,
    betToken: bet.betToken,
    live: bet.live,
    selectedCollateralSymbol: bet.selectedCollateralSymbol,
    line: bet.line,
    playerId: bet.playerId,
    playerName: bet.playerName,
  }
}

const createOvertimeComboDiscordEmbed = (
  bets: OvertimeBetData[],
  redirectUrl: string
): OvertimeDiscordEmbed => {
  try {
    const { ticketId, betAmount, chain, selectedCollateralSymbol, betToken } =
      bets[0] || {}

    // Calculate total odds by multiplying all individual bet odds
    const calculatedTotalOdds = bets.reduce((totalOdds, bet) => {
      const betOdds = bet.market?.odds?.[bet.position] || bet.totalOdds || 1
      return totalOdds * betOdds
    }, 1)

    return {
      title: `COMBO BET (x${bets.length})`,
      description: "",
      fields: [
        {
          name: "💰 Bet Amount",
          value: `${betAmount || "0"} ${selectedCollateralSymbol || betToken || "USDC"}`,
          inline: true,
        },
        {
          name: "🎲 Total Odds",
          value: `${formatMarketOdds(OddsType.DECIMAL, calculatedTotalOdds)}`,
          inline: true,
        },
        { name: "\u200B", value: "\u200B", inline: true },
        {
          name: "🆔 Bet ID",
          value: ticketId,
          inline: true,
        },
        { name: "\u200B", value: "\u200B", inline: true },
        {
          name: "Network",
          value: chain?.name as string,
          inline: false,
        },
        {
          name: "",
          value: `📨 [Copy bet](${redirectUrl})`,
          inline: false,
        },
        {
          name: "",
          value:
            "*Copy bet functionality only works for currently active markets*",
          inline: false,
        },
      ],
      color: 0x9b59b6, // Purple color to distinguish from Azuro
      footer: {
        text: "DGbet",
        icon_url: "https://sportsbook.dgbet.fun/images/logo.webp",
      },
    }
  } catch (error) {
    console.error("Error creating v2 Combo Discord embed:", error)
    // Return a basic embed if there's an error
    return {
      title: `Combo Bet (x${bets.length})`,
      description: "Error creating embed",
      fields: [
        {
          name: "Status",
          value: "Error occurred while creating embed",
          inline: false,
        },
      ],
      color: 0x9b59b6,
      footer: {
        text: "DGbet",
        icon_url: "https://sportsbook.dgbet.fun/images/logo.webp",
      },
    }
  }
}

const createOvertimeDiscordEmbed = (
  bet: OvertimeBetData,
  redirectUrl: string,
  matchUrl: string
): OvertimeDiscordEmbed => {
  try {
    const marketName = getTitleText(bet.market as any)
    const selectionName = getPositionTextV2(
      bet.market as any,
      bet.position,
      true
    )

    const odds = bet.market?.odds?.[bet.position] || bet.totalOdds || 0

    return {
      title: `${bet.market?.sport?.name || ""} > ${bet.market?.country?.name || ""} > ${
        bet.market?.league?.name || ""
      } ${bet.live ? "🔴 (LIVE)" : ""}`,
      description: `**[${
        bet.market?.participants?.map(p => p.name).join(" vs ") ||
        `${bet.homeTeam} vs ${bet.awayTeam}`
      }](${matchUrl})**`,
      fields: [
        {
          name: "🎯 Market",
          value: `**${marketName}**`,
          inline: true,
        },
        {
          name: "📌 Selection",
          value: `**${selectionName}**`,
          inline: true,
        },
        { name: "\u200B", value: "\u200B", inline: true },
        {
          name: "💰 Bet Amount",
          value: `${bet.betAmount} ${bet.selectedCollateralSymbol || bet.betToken}`,
          inline: true,
        },
        {
          name: "🎲 Odds",
          value: `${formatMarketOdds(OddsType.DECIMAL, odds)}`,
          inline: true,
        },
        { name: "\u200B", value: "\u200B", inline: true },
        {
          name: "🆔 Bet ID",
          value: bet.ticketId,
          inline: true,
        },
        { name: "\u200B", value: "\u200B", inline: true },
        {
          name: "Network",
          value: bet.chain?.name as string,
          inline: false,
        },
        {
          name: "",
          value: `📨 [Copy bet](${redirectUrl})`,
          inline: false,
        },
        {
          name: "",
          value:
            "*Copy bet functionality only works for currently active markets*",
          inline: false,
        },
      ],
      color: 0x9b59b6, // Purple color to distinguish from Azuro
      footer: {
        text: "DGbet",
        icon_url: "https://sportsbook.dgbet.fun/images/logo.webp",
      },
    }
  } catch (error) {
    console.error("Error creating v2 Discord embed:", error)
    // Return a basic embed if there's an error
    return {
      title: "Bet",
      description: `${bet.homeTeam} vs ${bet.awayTeam}`,
      fields: [
        {
          name: "💰 Bet Amount",
          value: `${bet.betAmount} ${bet.selectedCollateralSymbol || bet.betToken}`,
          inline: true,
        },
        {
          name: "🎲 Odds",
          value: `${bet.totalOdds.toFixed(2)}`,
          inline: true,
        },
        {
          name: "Network",
          value: bet.chain?.name as string,
          inline: true,
        },
        {
          name: "🆔 Bet ID",
          value: bet.ticketId,
          inline: true,
        },
      ],
      color: 0x9b59b6,
      footer: {
        text: "DGbet",
        icon_url: "https://sportsbook.dgbet.fun/images/logo.webp",
      },
    }
  }
}

const compressOvertimeData = (data: any, isSingle: boolean) => {
  try {
    const compressedData = isSingle
      ? {
          g: data.gameId, // gameId
          l: data.leagueId, // leagueId
          t: data.typeId, // typeId
          p: data.position, // position
          h: data.homeTeam, // homeTeam
          a: data.awayTeam, // awayTeam
          c: data.chain?.id, // chainId
          m: data.market, // market
          ba: data.betAmount, // betAmount
          to: data.totalOdds, // totalOdds
          bt: data.betToken, // betToken
          li: data.live, // live
          sc: data.selectedCollateralSymbol, // selectedCollateralSymbol
          ln: data.line, // line
          pi: data.playerId, // playerId
          pn: data.playerName, // playerName
        }
      : data.map((bet: any) => ({
          g: bet.gameId,
          l: bet.leagueId,
          t: bet.typeId,
          p: bet.position,
          h: bet.homeTeam,
          a: bet.awayTeam,
          c: bet.chain?.id,
          m: bet.market,
          ba: bet.betAmount,
          to: bet.totalOdds,
          bt: bet.betToken,
          li: bet.live,
          sc: bet.selectedCollateralSymbol,
          ln: bet.line,
          pi: bet.playerId,
          pn: bet.playerName,
        }))

    // Compress using GZIP like the original function
    const compressed = deflate(JSON.stringify(compressedData))

    // Convert to URL-safe base64
    return Buffer.from(compressed)
      .toString("base64")
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "")
  } catch (error) {
    console.error("Overtime compression error:", error)
    throw error
  }
}

export {
  createOvertimeComboDiscordEmbed,
  createOvertimeDiscordEmbed,
  createOvertimeBetslipData,
  compressOvertimeData,
}

export type { OvertimeBetData, OvertimeDiscordEmbed }
